import Add from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { ChangeEvent, useEffect, useState } from "react";

import { BotOptionSpec } from "features/adminUsers/adminUsers";
import {
  OPTION_KEYS,
  parseOptions,
  readListOption,
  readMapOption,
  readOption,
  writeListOption,
  writeMapOption,
  writeOption,
} from "utils/additionalOptions";

import { defaultHint, formatProblem } from "./BotOptionSummary";

type Props = {
  /** What this bot declares it reads (BotRegistry.metadata.options). */
  options: BotOptionSpec[];
  /** Raw `additional_options`; these fields edit keys inside it. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  /** Keys a dedicated control elsewhere in the dialog already edits: not
   * rendered here, but still treated as declared so they stay out of the raw
   * list. Two inputs writing one key is how DOCS_TYPE got double-encoded. */
  omitKeys?: string[];
};

// What a required switch stores for each answer, and how the form shows it.
const SWITCH_ANSWERS: Record<string, string> = { True: "On", False: "Off" };
// A switch in its risky position (spec.warn_when): label and help in crimson,
// a lighter one on the dark theme so the small help text stays readable.
const WARNING_COLOR = (theme: Theme) =>
  theme.palette.mode === "dark" ? "#FF5C77" : "#DC143C";

function FieldFrame({
  spec,
  children,
}: {
  spec: BotOptionSpec;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {spec.label}
        {spec.required ? " *" : ""}
      </Typography>
      {spec.help ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5 }}
        >
          {spec.help}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function ListField({
  spec,
  items,
  onChange,
  disabled,
}: {
  spec: BotOptionSpec;
  items: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const rows = items.length ? items : [""];
  return (
    <FieldFrame spec={spec}>
      {rows.map((item, i) => (
        <Stack
          key={i}
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mb: 0.75 }}
        >
          <TextField
            size="small"
            fullWidth
            value={item}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(rows.map((v, j) => (j === i ? e.target.value : v)))
            }
          />
          <IconButton
            size="small"
            color="error"
            disabled={disabled || (rows.length === 1 && !rows[0])}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<Add fontSize="small" />}
        disabled={disabled}
        onClick={() => onChange([...rows, ""])}
      >
        Add
      </Button>
    </FieldFrame>
  );
}

function MapField({
  spec,
  rows,
  onChange,
  disabled,
}: {
  spec: BotOptionSpec;
  rows: [string, string][];
  onChange: (next: [string, string][]) => void;
  disabled?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const masked = Boolean(spec.secret) && !revealed;
  return (
    <FieldFrame spec={spec}>
      {rows.map(([k, v], i) => (
        <Stack
          key={i}
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mb: 0.75 }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={spec.key_label || "Key"}
            inputProps={{ autoComplete: "off" }}
            value={k}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(
                rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)) as [
                  string,
                  string,
                ][],
              )
            }
          />
          <TextField
            size="small"
            fullWidth
            placeholder={spec.value_label || "Value"}
            // new-password keeps the browser from filling in a saved login here.
            type={masked ? "password" : "text"}
            inputProps={{ autoComplete: spec.secret ? "new-password" : "off" }}
            value={v}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(
                rows.map((r, j) => (j === i ? [r[0], e.target.value] : r)) as [
                  string,
                  string,
                ][],
              )
            }
          />
          <IconButton
            size="small"
            color="error"
            disabled={
              disabled || (rows.length === 1 && !rows[0][0] && !rows[0][1])
            }
            onClick={() =>
              onChange(rows.filter((_, j) => j !== i) as [string, string][])
            }
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<Add fontSize="small" />}
        disabled={disabled}
        onClick={() => onChange([...rows, ["", ""]] as [string, string][])}
      >
        Add row
      </Button>
      {spec.secret ? (
        <Button
          size="small"
          startIcon={
            revealed ? (
              <VisibilityOffIcon fontSize="small" />
            ) : (
              <VisibilityIcon fontSize="small" />
            )
          }
          onClick={() => setRevealed((shown) => !shown)}
        >
          {revealed ? "Hide" : "Show"}
        </Button>
      ) : null}
    </FieldFrame>
  );
}

/** Renders whatever the selected bot declares it reads. */
export default function BotOptionFields({
  options,
  value,
  onChange,
  disabled,
  omitKeys,
}: Props) {
  const omitted = new Set((omitKeys ?? []).map((k) => k.toUpperCase()));
  const specs = options.filter((o) => !omitted.has(o.key.toUpperCase()));
  // Lists and map rows are local only once the user edits them: a half-typed
  // row cannot be represented in the stored value. Until then they are read
  // from the value on every render - the specs arrive from the registry after
  // the dialog is already open, and a mount-time snapshot rendered every field
  // empty on the first open.
  const [lists, setLists] = useState<Record<string, string[]>>({});
  const [showRaw, setShowRaw] = useState(false);
  const [maps, setMaps] = useState<Record<string, [string, string][]>>({});

  // An option declared with `prefill` gets its default written into the job as
  // soon as the form shows it unset - a new job, or one saved before the option
  // existed - so what the form shows is what the job will carry. Never while
  // the form is read-only (a running job's config is locked).
  useEffect(() => {
    if (disabled) return;
    const hidden = new Set((omitKeys ?? []).map((k) => k.toUpperCase()));
    let next = value;
    for (const spec of options) {
      if (
        !spec.prefill ||
        spec.default == null ||
        hidden.has(spec.key.toUpperCase())
      )
        continue;
      if ((readOption(next, spec.key) ?? "").trim()) continue;
      next = writeOption(next, spec.key, String(spec.default));
    }
    if (next !== value) onChange(next);
  }, [options, omitKeys, value, disabled, onChange]);

  // Undeclared keys still have to be reachable: hand-configured jobs carry them.
  const declared = new Set([
    ...options.map((o) => o.key.toUpperCase()),
    ...Array.from(omitted),
    OPTION_KEYS.skipErrorEmployees,
  ]);
  const undeclared = parseOptions(value).filter(
    (t) => !declared.has(t.key.toUpperCase()),
  );

  if (!specs.length && !undeclared.length) return null;

  return (
    <>
      {specs.map((spec) => {
        if (spec.type === "list") {
          return (
            <ListField
              key={spec.key}
              spec={spec}
              items={lists[spec.key] ?? readListOption(value, spec.key)}
              disabled={disabled}
              onChange={(next) => {
                setLists((prev) => ({ ...prev, [spec.key]: next }));
                onChange(writeListOption(value, spec.key, next));
              }}
            />
          );
        }
        if (spec.type === "map") {
          const stored = Object.entries(readMapOption(value, spec.key)) as [
            string,
            string,
          ][];
          const rows = maps[spec.key] ?? (stored.length ? stored : [["", ""]]);
          return (
            <MapField
              key={spec.key}
              spec={spec}
              rows={rows}
              disabled={disabled}
              onChange={(next) => {
                const safe = next.length
                  ? next
                  : ([["", ""]] as [string, string][]);
                setMaps((prev) => ({ ...prev, [spec.key]: safe }));
                onChange(
                  writeMapOption(value, spec.key, Object.fromEntries(next)),
                );
              }}
            />
          );
        }
        if (spec.type === "bool" && spec.required) {
          // A switch left alone stores nothing, so a required one would show an
          // "off" the job never carries: it asks On or Off like a required select.
          const raw = (readOption(value, spec.key) || "").toLowerCase();
          const answer =
            raw === "true" ? "True" : raw === "false" ? "False" : "";
          return (
            <FieldFrame key={spec.key} spec={spec}>
              <TextField
                size="small"
                fullWidth
                select
                value={answer}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) =>
                    selected ? (
                      SWITCH_ANSWERS[String(selected)]
                    ) : (
                      <Box component="span" sx={{ color: "text.disabled" }}>
                        Pick one
                      </Box>
                    ),
                }}
                disabled={disabled}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(writeOption(value, spec.key, e.target.value || null))
                }
              >
                {Object.entries(SWITCH_ANSWERS).map(([stored, shown]) => (
                  <MenuItem key={stored} value={stored}>
                    {shown}
                  </MenuItem>
                ))}
              </TextField>
            </FieldFrame>
          );
        }
        if (spec.type === "bool") {
          const raw = (readOption(value, spec.key) || "").toLowerCase();
          const checked = raw ? raw === "true" : Boolean(spec.default);
          // The label and its help follow the switch: in the position the spec
          // calls risky (low speed off, unsafe hours allowed) both turn crimson
          // and the help says what that position costs.
          const warning =
            spec.warn_when !== undefined && checked === spec.warn_when;
          const help = warning && spec.warn_help ? spec.warn_help : spec.help;
          return (
            <FormControlLabel
              key={spec.key}
              data-warning={warning ? "true" : undefined}
              // Switch against the label's first line, not centred against a
              // three-line help text, and the label typeset like every other
              // field kind's: name in bold, help as a caption under it. Glued
              // into one grey paragraph they read as a wall of small text.
              sx={{
                mb: 1.5,
                ml: 0,
                alignItems: "flex-start",
                "& .MuiFormControlLabel-label": { mt: 0.5 },
              }}
              control={
                <Switch
                  size="small"
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange(
                      writeOption(
                        value,
                        spec.key,
                        e.target.checked ? "True" : "False",
                      ),
                    )
                  }
                />
              }
              label={
                <Box sx={{ maxWidth: "68ch" }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={warning ? { color: WARNING_COLOR } : undefined}
                  >
                    {spec.label}
                  </Typography>
                  {help ? (
                    <Typography
                      variant="caption"
                      color={warning ? undefined : "text.secondary"}
                      sx={{
                        display: "block",
                        ...(warning ? { color: WARNING_COLOR } : {}),
                      }}
                    >
                      {help}
                    </Typography>
                  ) : null}
                </Box>
              }
            />
          );
        }
        const isSelect = Boolean(spec.choices?.length);
        // A select puts a placeholder on its hidden native input, so the
        // default is drawn as the empty value instead. It is still a hint and
        // never stored: the "(default)" entry clears a picked value back to it.
        // A required option shows no default, which would read as filled in.
        const unset = (
          <Box component="span" sx={{ color: "text.disabled" }}>
            {spec.required ? "Pick one" : (defaultHint(spec) ?? "not set")}
          </Box>
        );
        const problem = formatProblem(spec, readOption(value, spec.key));
        return (
          <FieldFrame key={spec.key} spec={spec}>
            <TextField
              size="small"
              fullWidth
              select={isSelect}
              type={!isSelect && spec.type === "int" ? "number" : "text"}
              value={readOption(value, spec.key) ?? ""}
              error={Boolean(problem)}
              helperText={problem ?? undefined}
              inputProps={
                spec.format ? { inputMode: "numeric", maxLength: 8 } : undefined
              }
              placeholder={
                !isSelect && spec.default != null ? String(spec.default) : ""
              }
              SelectProps={
                isSelect
                  ? {
                      displayEmpty: true,
                      renderValue: (selected) =>
                        selected ? String(selected) : unset,
                    }
                  : undefined
              }
              disabled={disabled}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(
                  writeOption(value, spec.key, e.target.value.trim() || null),
                )
              }
            >
              {isSelect && !spec.required ? (
                <MenuItem value="">{unset}</MenuItem>
              ) : null}
              {(spec.choices ?? []).map((choice) => (
                <MenuItem key={choice} value={choice}>
                  {choice}
                </MenuItem>
              ))}
            </TextField>
          </FieldFrame>
        );
      })}

      {undeclared.length ? (
        <Box sx={{ mb: 1.5 }}>
          <Button size="small" onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? "Hide" : "Show"} {undeclared.length} option(s) this bot
            does not declare
          </Button>
          {showRaw ? (
            <TextField
              sx={{ mt: 0.5 }}
              fullWidth
              multiline
              minRows={2}
              label="Raw options"
              value={value}
              disabled={disabled}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(e.target.value)
              }
            />
          ) : null}
        </Box>
      ) : null}
    </>
  );
}
